import { type ReactNode, useCallback } from "react";

import toast from "react-hot-toast";

import { Segment } from "semantic-ui-react";
import uuid from "uuid";

import util from "utils/utils";

import { FileChooserWithDropzone } from "components/lib/Choosers";

import { FieldProps } from "./FieldProps";
import { FileComponent } from "./File";

type DropZoneProps = {
  children: React.ReactNode;
  files: Array<{ name: string; value: string }>;
  onRemove: (fileId: string) => void;
  onClick: () => void;
};

const DropZone = ({ children, files, onRemove, onClick }: DropZoneProps) => {
  return (
    <Segment placeholder onClick={onClick}>
      {children}
      {files?.length > 0 ?
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <FileComponent files={files} onRemove={onRemove} />
        </div>
      : null}
    </Segment>
  );
};

const MultiFile = ({
  field,
  fieldValue = [],
  updateIdea,
  isEditing,
  ideaId,
}: FieldProps) => {
  const addFiles = useCallback(
    (
      files: {
        savedName: string;
        fileName: string;
        signedDownloadRequest: string;
      }[],
    ) => {
      if (field.maxFiles && fieldValue.length + files.length > field.maxFiles) {
        toast.error("Your uploads exceed the maximum number of files allowed");
        return;
      }

      const updatedFiles = [
        ...files.map((file) => ({
          value: file.signedDownloadRequest,
          name: file.fileName,
          _id: uuid.v4(),
        })),
        ...fieldValue,
      ];
      updateIdea(updatedFiles, ["templated", field.id]);
    },
    [field.id, field.maxFiles, fieldValue, updateIdea],
  );

  const removeFile = useCallback(
    (fileId: string) => {
      util
        .confirm(
          "Are you sure you want to delete this file?",
          "This action cannot be undone, but you can always re-upload the file.",
        )
        .then(() => {
          const updatedImages = fieldValue.filter(
            (file) => file._id !== fileId,
          );
          updateIdea(updatedImages, ["templated", field.id]);
        })
        .catch(() => {});
    },
    [fieldValue, updateIdea, field.id],
  );

  const hasMaxFiles = field.maxFiles && fieldValue.length >= field.maxFiles;

  if (!isEditing) {
    return <FileComponent files={fieldValue} />;
  }

  const MultiFileDropzone = ({
    children,
    onClick,
  }: {
    children: ReactNode;
    onClick: () => void;
  }) => (
    <DropZone files={fieldValue} onRemove={removeFile} onClick={onClick}>
      {children}
    </DropZone>
  );

  return (
    <div>
      <FileChooserWithDropzone
        forType="idea"
        forId={ideaId}
        allowMultiple={true}
        onCompleteMultiple={addFiles}
        onError={toast.error}
        popupProps={{ position: "bottom center", disabled: hasMaxFiles }}
        dropzoneProps={{
          title: "Upload multiple files",
          subtitle:
            field.maxFiles &&
            `Maximum of ${util.pluralise(field.maxFiles, "file", "files")}`,
          DropZoneComponent: MultiFileDropzone,
          defaultIcon: "folder",
        }}
      />
    </div>
  );
};

export default MultiFile;
